/*
 * SIGN-IN and CREATE ACCOUNT (direction §12).
 *
 * "Three methods, equal footing, local first: email/password (filled-red
 * primary), passkey (outline), then Google/Apple below a divider. Standard
 * autocomplete attributes, no paste-blocking, single-screen flow.
 * Create-account and forgot-password as text links."
 *
 * THE PASSWORD-MANAGER ACCEPTANCE CRITERION (§2: "every credential flow works
 * with password managers") is the reason several things here look plainer than
 * they might. Each is a rule, and each is a thing this file must never grow:
 *
 *   1. A REAL <form> WITH A SUBMIT BUTTON. Managers detect a login form by its
 *      shape. A div with a click handler is invisible to them, and it also breaks
 *      the Enter key.
 *   2. BOTH FIELDS ON ONE SCREEN, always. §12 says single-screen flow, and the
 *      reason is mechanical: an email-then-password wizard is the pattern that
 *      breaks autofill, because the manager cannot fill a password field that is
 *      not in the DOM yet.
 *   3. STANDARD autocomplete VALUES. `username`, `current-password`,
 *      `new-password` — the exact tokens. `autocomplete="off"` on a credential
 *      field is ignored by most managers and disrespected by all users.
 *   4. NO PASTE BLOCKING. There is no onPaste handler in this file and there must
 *      never be one: blocking paste stops a generated 32-character password from
 *      being used at all, which makes the account LESS secure.
 *   5. name= ON EVERY FIELD. Some managers key their heuristics off the name
 *      attribute rather than the id.
 *   6. THE EMAIL FIELD IS type="email" AND THE PASSWORD type="password", plainly.
 *      No custom masking, no split character boxes.
 *
 * FORGOT-PASSWORD IS A TEXT LINK, as §12 lists it, in sign-in mode only (the
 * create-account form has no password to forget). A LINK and not a button,
 * because it navigates to its own page, /forgot-password (decision 607).
 */

import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";

import { Brand } from "../components/Brand";
import { ApiError, api, list, type Provider, type User } from "../lib/api";
import { createPasskey, getPasskeyAssertion, passkeysSupported, wasCancelled } from "../lib/webauthn";
import styles from "./SignIn.module.css";

export interface SignInProps {
  mode: "sign-in" | "create-account";
  onSignedIn: (user: User) => void;
}

export function SignIn({ mode, onSignedIn }: SignInProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Create-account only, and never sent anywhere: see the submit handler.
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState(false);

  // Which methods actually exist. Presence implies state, applied to a sign-in
  // method exactly as it applies to a capability pill: an unconfigured provider
  // is not offered, because a button that leads to an error page is worse than no
  // button (decision 309).
  const [providers, setProviders] = useState<Provider[]>([]);
  const [passkeysConfigured, setPasskeysConfigured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .providers()
      .then((p) => {
        if (cancelled) return;
        setProviders(list(p.oidc));
        setPasskeysConfigured(p.passkey);
      })
      .catch(() => {
        // The sign-in form still works without knowing about the optional
        // methods, so a failure here degrades rather than blocks.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const creating = mode === "create-account";

  /**
   * Go to "/" once signed in, so the LANDING DECISION runs.
   *
   * Signing in does not change the URL by itself: the signed-out router serves
   * this form from `path="*"`, so someone who arrived on `/active` — a bookmark, a
   * deep link, a reload of the tab they left open — is still on `/active` when the
   * gate flips. React Router then renders that route directly and `/` is never
   * visited.
   *
   * That was a real bug, and a nasty one because the screen it broke was the
   * screen being displayed. `Landing` is the ONLY thing that reports the live-event
   * count after a cold `/api/me`, and the shell hides the Active tab while that
   * count is zero (decision 324) — so signing in on `/active` rendered Active with
   * no Active tab, and no way back to it once you navigated away, because nothing
   * else ever reports the count. Only a reload fixed it.
   *
   * So this is not a redirect for tidiness; it is what keeps the shell's count and
   * the screen in agreement. main.tsx's comment already asserted that the landing
   * decision "runs on every sign-in" — this is what makes that true.
   *
   * `replace`, so the deep link the user arrived on does not sit in history as a
   * back target that bounces them through the gate again. And the landing decision
   * replaces in turn, so signing in adds no history entries at all.
   *
   * NOT a redirect back to the path they arrived on, deliberately: Routes.tsx
   * records that the landing rule is a better answer than a bookmarked URL, since
   * it sends people to the screen that has something on it.
   */
  function landAfterSignIn() {
    navigate("/", { replace: true });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMismatch(false);

    /*
     * THE CONFIRMATION IS REFUSED BEFORE ANY REQUEST (roadmap stage 2 item 5).
     * Nothing is sent, so a typo in a password nobody can read back costs a
     * corrected field rather than an account with a password its owner does not
     * know. Reset exists now (decision 607), but it costs an email round trip
     * that this check makes unnecessary.
     *
     * DISPLAY AND FLOW ONLY: the wire keeps a single password field
     * (decisions 307, 367). The confirmation's whole job is done the moment the
     * two agree, so sending it would be asking the server to re-check something
     * that can no longer be false.
     */
    if (creating && password !== confirmPassword) {
      setMismatch(true);
      return;
    }

    setBusy(true);
    try {
      const body = creating
        ? await api.register(email, password, displayName || email)
        : await api.signIn(email, password);
      onSignedIn(body.user as User);
      landAfterSignIn();
    } catch (err) {
      setError(messageFor(err, t));
    } finally {
      setBusy(false);
    }
  }

  async function withPasskey() {
    setError(null);
    setBusy(true);
    try {
      if (creating) {
        // Registering a passkey needs an account to attach it to, so this path is
        // only reachable once signed in — Settings offers it. Here it would be a
        // dead end, which is why the button is not rendered in create mode.
        const begun = await api.beginPasskeyRegistration();
        const credential = await createPasskey(begun.options);
        await api.finishPasskeyRegistration(begun.ceremony_id, credential);
        navigate("/");
        return;
      }
      const begun = await api.beginPasskeyLogin();
      const assertion = await getPasskeyAssertion(begun.options);
      const body = await api.finishPasskeyLogin(begun.ceremony_id, assertion);
      onSignedIn(body.user as User);
      landAfterSignIn();
    } catch (err) {
      // A cancelled ceremony is not a failure. §4's voice rules mean the app does
      // not tell someone "that didn't work" when they deliberately dismissed the
      // system sheet.
      if (!wasCancelled(err)) setError(messageFor(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        {/*
          The ANIMATED lockup, at the width of the form beneath it.

          §8 allows the play-once animation at sign-in — one of its two places —
          and this screen used to decline it, because the only animated master was
          mark-only and a first-time visitor needs to read the name more than they
          need the flourish (deviation #8). An animated lockup master now exists,
          so both are available at once and there is nothing left to trade.

          It NEVER gates content: the form below is interactive from the first
          frame, and the wordmark itself does not animate, so the name is readable
          before the bars have finished arriving.

          `fluid` rather than a height: this is the one place the logo is the
          largest thing on screen rather than a fixture in a bar, and sizing it to
          the container is what keeps it the same width as the fields under it on
          every screen rather than at one chosen breakpoint.
        */}
        <div className={styles.brand}>
          <Brand variant="lockup" animated fluid />
        </div>

        <h1 className={styles.heading}>{creating ? t("auth.createHeading") : t("auth.signInHeading")}</h1>

        {/*
          One form, both fields, always in the DOM. See the header — this shape is
          the acceptance criterion, not a stylistic choice.
        */}
        <form className={styles.form} onSubmit={submit} noValidate={false}>
          {creating ? (
            <label className={styles.field}>
              <span className={styles.label}>{t("auth.displayName")}</span>
              <input
                className={styles.input}
                type="text"
                name="name"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                // Not required: the server falls back to the email, and a field
                // that blocks submission for a cosmetic value is friction for
                // nothing.
              />
            </label>
          ) : null}

          <label className={styles.field}>
            <span className={styles.label}>{t("auth.email")}</span>
            <input
              className={styles.input}
              type="email"
              name="email"
              // `username`, not `email`: it is the token managers key on for the
              // identifier field of a login form.
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <div className={styles.field}>
            {/*
              The label wraps only the input, and the hint sits OUTSIDE it, tied on
              with aria-describedby.

              Not a nicety: a hint inside the <label> becomes part of the field's
              accessible NAME, so the password field announced as "Password At
              least 12 characters. A password manager is a good idea." A name is
              what the field IS; a description is advice about it, and conflating
              them makes every screen-reader announcement of this field a
              paragraph. Caught by the test that looks the field up by its label.
            */}
            <label className={styles.label} htmlFor="password">
              {t("auth.password")}
            </label>
            <input
              id="password"
              className={styles.input}
              type="password"
              name="password"
              {...(creating ? { "aria-describedby": "password-hint" } : {})}
              // The one place these two modes must differ: `new-password` is what
              // makes a manager OFFER to generate one, and `current-password` is
              // what makes it fill the saved one. Getting them the wrong way round
              // produces a manager that suggests a new password at sign-in.
              autoComplete={creating ? "new-password" : "current-password"}
              required
              minLength={creating ? 12 : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {creating ? (
              <span id="password-hint" className={styles.hint}>
                {t("auth.passwordHint")}
              </span>
            ) : null}
          </div>

          {/*
            CONFIRM, on create-account only (roadmap stage 2 item 5). Not on
            sign-in: there the password is checked against one that already
            exists, so a typo is answered immediately and asking twice would be
            friction for nothing.

            `new-password` ON THIS FIELD TOO, which is the non-obvious part. It
            is the token that makes a manager treat the pair as one
            generate-and-save flow and fill both — with `off`, or with no token,
            several managers fill the first field and leave this one empty,
            which turns the manager into the thing that fails the form. It is
            also rule 3 in this file's header: the exact standard tokens, always.
          */}
          {creating ? (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="confirm-password">
                {t("auth.confirmPassword")}
              </label>
              <input
                id="confirm-password"
                className={styles.input}
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                {...(mismatch ? { "aria-describedby": "confirm-password-mismatch" } : {})}
                required
                // NO minLength HERE, deliberately, though the field above has
                // one. The floor is a property of the password; this field's
                // only job is to equal it, and a length constraint on it means
                // a short mismatch is answered by the BROWSER's bubble
                // ("please lengthen this text") instead of by our sentence —
                // which is both the wrong diagnosis and not our words (§4).
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  // Cleared as soon as they start fixing it. Leaving the
                  // sentence up while someone retypes reads as an error that
                  // will not go away.
                  setMismatch(false);
                }}
              />
              {/*
                A PLAIN SENTENCE, deliberately WITHOUT role="alert" — unlike the
                submit error below it. This is not news arriving from elsewhere:
                the reader is looking at the field they just left, and an alert
                interrupts a screen reader mid-word to announce something they
                are already on. It is tied to the input by aria-describedby
                instead, so it is read as part of the field.
              */}
              {mismatch ? (
                <span id="confirm-password-mismatch" className={styles.error}>
                  {t("auth.passwordMismatch")}
                </span>
              ) : null}
            </div>
          ) : null}

          {/*
            The error, in a live region so a screen reader hears it without the
            focus moving — moving focus on a failed sign-in loses the user's place
            in the form.
          */}
          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}

          {/* FILLED RED = PRIMARY ACTION (principle 3), and only here. */}
          <button className={styles.primary} type="submit" disabled={busy}>
            {creating ? t("auth.createAction") : t("auth.signInAction")}
          </button>
        </form>

        {!creating ? (
          <p className={styles.forgot}>
            <Link to="/forgot-password">{t("auth.forgotLink")}</Link>
          </p>
        ) : null}

        {/* The passkey, outline — equal footing, second by convention not rank. */}
        {!creating && passkeysConfigured && passkeysSupported() ? (
          <button className={styles.outline} type="button" onClick={withPasskey} disabled={busy}>
            {t("auth.withPasskey")}
          </button>
        ) : null}

        {/*
          The divider and the OIDC buttons render only when something is behind
          them (decision 309). An empty divider announcing nothing is the visual
          equivalent of a placeholder.
        */}
        {providers.length > 0 ? (
          <>
            <div className={styles.divider}>
              <span>{t("auth.or")}</span>
            </div>
            {providers.map((p) => (
              <a
                key={p.name}
                className={styles.outline}
                /*
                  A REAL LINK, because this is a navigation to another origin: the
                  OIDC flow is a top-level redirect and must be, since an XHR
                  cannot complete a consent screen.

                  The href is the server's own `start_url` rather than a path built
                  here. A URL in a payload is a promise (contract §0) — and the
                  server is the only party that knows whether that provider is
                  actually configured.
                */
                href={p.start_url}
              >
                {t(`auth.withProvider.${p.name}`)}
              </a>
            ))}
          </>
        ) : null}

        {/*
          Create-account as a TEXT LINK (§12), and a real route rather than a
          toggle: it is a different task with a different form, and a URL for it
          means a browser can remember which one you were on.
        */}
        <p className={styles.alt}>
          {creating ? (
            <>
              {t("auth.haveAccount")} <Link to="/sign-in">{t("auth.signInAction")}</Link>
            </>
          ) : (
            <>
              {t("auth.noAccount")} <Link to="/create-account">{t("auth.createAction")}</Link>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Turns a failure into a sentence.
 *
 * OUR WORDS, NEVER THE SERVER'S. §4 makes user-facing copy localized and ours;
 * `detail` from an error envelope is a developer string in one language. The
 * mapping is on the STATUS, which is the part of an HTTP failure that carries
 * meaning.
 *
 * And note what 401 says: "those details don't match", never "no account with
 * that email". Confirming which half was wrong tells an attacker which addresses
 * are registered.
 */
function messageFor(err: unknown, t: (k: string) => string): string {
  if (!(err instanceof ApiError)) return t("auth.errorGeneric");

  switch (err.failure.kind) {
    case "unauthenticated":
      return t("auth.errorCredentials");
    case "offline":
      return t("auth.errorOffline");
    case "server":
      if (err.failure.status === 409) return t("auth.errorEmailTaken");
      if (err.failure.status === 422 || err.failure.status === 400) {
        return t("auth.errorInvalid");
      }
      return t("auth.errorGeneric");
    default:
      return t("auth.errorGeneric");
  }
}
